/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 22-09-2026
 *
 * Het beheer van het model als geheel (golf 4c): nieuw, opslaan, kopiëren, de naam wijzigen, laden,
 * meekijken, van project wisselen, uitloggen en de samenwerking opzetten. Dit zijn de flows die tot
 * Fase 4 in `cytocanvas.controller.js` stonden. Ze zitten bovenop de `OpslagService`: die weet hoe
 * een model opgeslagen en geladen wordt, deze service weet wanneer en in welke volgorde, met welke
 * dialoog ertussen.
 */
import {computed, inject, Injectable, signal} from '@angular/core';
import {BsModalService} from 'ngx-bootstrap/modal';
import {TranslateService} from '@ngx-translate/core';
import {LocalConfig, Sihwlog, SihwconfirmService} from 'sihw-ngx';
import {ApiService} from '../backend/api.service';
import type {ModelId} from '../backend/api.service';
import {DlModel} from './dl-model';
import {OpslagService} from './opslag.service';
import {SamenwerkprotocolService} from '../samenwerking/samenwerkprotocol.service';
import {SimulatieService} from '../simulatie/simulatie.service';
import {SimulatieStatusService} from '../../services/simulatie-status.service';
import {SamenwerkingService} from '../../services/samenwerking.service';
import type {SamenwerkState} from '../../services/samenwerking.service';
import {SessieService} from '../../services/sessie.service';
import {VideoStatusService} from '../../services/video-status.service';
import {FoutmeldingService} from '../../services/foutmelding.service';
import {ToastService} from '../../services/toast.service';
import {Dashboardv1Service} from '../../services/dashboardv1.service';
import {Dashboardv2Service} from '../../services/dashboardv2.service';
import {DlModelLoaderComponent} from '../../components/dialogs/dl-model-loader/dl-model-loader.component';
import type {ModelKeuze, ModelSamenvatting} from '../../components/dialogs/dl-model-loader/model-loader.types';
import {DlTemplatePickerComponent} from '../../components/dialogs/dl-template-picker/dl-template-picker.component';
import type {TemplateKeuze, TemplateSamenvatting} from '../../components/dialogs/dl-template-picker/template-picker.types';
import {DlStartupPopupComponent} from '../../components/dialogs/dl-startup-popup/dl-startup-popup.component';
import type {StartupPopupProjectdata, StartupPopupUserdata} from '../../components/dialogs/dl-startup-popup/startup-popup.types';

/**
 * Het model dat de ontwikkelomgeving bij het opstarten meteen laadt, uit `DL.quickLoad` in de
 * localconfig: één model, of per gebruikersnaam een model. Een model is een model-id of de titel
 * van een model van die gebruiker. Met `startSim` start daarna ook de simulatie.
 */
export interface QuickLoad {
    model: string | Record<string, string>;
    startSim?: boolean;
}

/**
 * Hoe lang de opslaanknop na een klik niets doet, in ms.
 */
const OPSLAAN_PAUZE_MS = 5000;

/**
 * De vorm van een model-id (een uuid); alles wat daar niet op lijkt, zoekt quickLoad als titel.
 */
const MODEL_ID_VORM = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

@Injectable({providedIn: 'root'})
export class ModelbeheerService {

    private readonly log = new Sihwlog('info', 'ModelbeheerService');
    private readonly opslag = inject(OpslagService);
    private readonly dlModel = inject(DlModel);
    private readonly api = inject(ApiService);
    private readonly sessie = inject(SessieService);
    private readonly samenwerking = inject(SamenwerkingService);
    private readonly samenwerkprotocol = inject(SamenwerkprotocolService);
    private readonly simulatie = inject(SimulatieService);
    private readonly simulatieStatus = inject(SimulatieStatusService);
    private readonly videoStatus = inject(VideoStatusService);
    private readonly foutmelding = inject(FoutmeldingService);
    private readonly toast = inject(ToastService);
    private readonly translate = inject(TranslateService);
    private readonly modalService = inject(BsModalService);
    private readonly sihwconfirm = inject(SihwconfirmService);
    private readonly dashboardv1 = inject(Dashboardv1Service);
    private readonly dashboardv2 = inject(Dashboardv2Service);

    /**
     * Of er een model geladen wordt. Het lege canvas tussen het wissen en het tekenen is dan geen
     * leeg model, en de emptystate en model-leeg blijven weg.
     */
    private readonly _laden = signal(false);

    /**
     * Of de opslaanknop nog even niets doet na de vorige klik.
     */
    private opslaanGepauzeerd = false;

    /**
     * Of er een model geladen wordt; zie `_laden`.
     */
    readonly laden = this._laden.asReadonly();

    /**
     * Of de samenwerkknop er is: het project staat samenwerken toe, of het model werkt al samen.
     */
    readonly magSamenwerken = computed(() => this.sessie.magSamenwerken() || this.samenwerking.werkSamen());

    /* ***************** DE OPSTART ***************** */

    /**
     * Start de modelomgeving op na het inloggen. `openModel` en `fromAdmin` openen het meegegeven
     * model als nieuw model (vanuit de backoffice en material); de rest valt op de gewone opstart:
     * quickLoad in de ontwikkelomgeving, anders de startup-popup of het standaardmodel met de
     * startvideo. De vroegere actie `saveMaterial` bestaat niet meer en valt daar ook onder.
     * @param [openAction] de actie uit de url (`#!/actie/<actie>/<arg>`)
     * @param [openArg] het argument daarbij: een model-id, of bij `fromAdmin` een toegangssleutel
     */
    async startOp(openAction?: string | null, openArg?: string | null): Promise<void> {
        switch (openAction) {
            case 'openModel':
            case 'fromAdmin':
                if (openArg) {
                    //als sjabloon: het backend geeft het dan ook aan wie het niet bezit
                    await this.opslag.nieuwUitSjabloon(openArg, openAction === 'fromAdmin');
                }
                return;
            default:
                await this.standaardOpstart();
        }
    }

    /**
     * Een actie uit de url in een venster dat al openstaat. De oude admin opent create met
     * `target="dynaladmin"`, dus een tweede klik op "open in create" verandert alleen de hash van een
     * draaiende create; dan hoort het model dat er staat eerst bewaard te worden (testronde 4c, test 26).
     * Lukt dat bewaren niet, dan blijft alles zoals het was.
     * @param [openAction] de actie uit de url
     * @param [openArg] het argument daarbij
     */
    async heropen(openAction?: string | null, openArg?: string | null): Promise<void> {
        if (!await this.bewaar()) {
            this.log.error('fout bij opslaan - de nieuwe actie uit de url wordt niet uitgevoerd');
            return;
        }
        this.stopSimulatie(true);
        await this.startOp(openAction, openArg);
    }

    /* ***************** DE FLOWS ***************** */

    /**
     * De knop "nieuw model". Bij een project met `forceer_start` begint de applicatie na het nieuwe
     * model opnieuw op. Anders de sjabloonkiezer als het project sjablonen of normmodellen heeft,
     * of meteen een leeg model.
     */
    async nieuwMenu(): Promise<void> {
        if (this.sessie.forceerStart()) {
            if (await this.nieuw()) {
                this.log.debug('Reload location');
                location.reload(); //zodat ook de userdata opnieuw is
            }
            return;
        }
        const sjablonen = await this.api.getAllTemplates();
        this.videoStatus.cancel();
        if (!sjablonen || !sjablonen.length) {
            if (await this.nieuw()) {
                //pas als hij helemaal klaar is, anders logt het nog in het oude model
                this.opslag.logaction('newmodel', 'model', null, 'model',
                    {modelleerniveau: this.dlModel.effectief_modelleerniveau}, false);
            }
            return;
        }
        const sjabloon = await this.openTemplatePicker(sjablonen);
        if (sjabloon === null) {
            return; //geannuleerd
        }
        if (await this.nieuw(sjabloon.id || undefined, 'norm' in sjabloon ? sjabloon.norm : undefined)) {
            this.opslag.logaction('newmodel', 'model', null, 'model', {
                template: sjabloon,
                modelleerniveau: this.dlModel.effectief_modelleerniveau
            }, false);
        }
    }

    /**
     * Begint een nieuw model. Het huidige wordt eerst opgeslagen; lukt dat niet, dan blijft alles
     * zoals het was. De simulatie gaat uit.
     * @param [sjabloonId] het model dat als sjabloon dient
     * @param [alsNorm] gebruik het sjabloon als normmodel in plaats van als beginstand
     * @returns of er een nieuw model is
     */
    async nieuw(sjabloonId?: ModelId, alsNorm?: boolean): Promise<boolean> {
        const gelukt = await this.bewaar();
        this.stopSimulatie(true);
        if (!gelukt) {
            return false; //gestopt wegens een opslagfout
        }
        if (!sjabloonId) {
            return this.opslag.nieuw();
        }
        return alsNorm ? this.opslag.nieuwMetNormmodel(sjabloonId) : this.opslag.nieuwUitSjabloon(sjabloonId);
    }

    /**
     * De opslaanknop. Een nog niet opgeslagen model vraagt eerst zijn eigenschappen. Na een klik
     * doet de knop even niets, zodat een dubbelklik geen twee modellen oplevert.
     * @returns of het model is opgeslagen; `false` ook bij annuleren en tijdens de pauze
     */
    async slaOp(): Promise<boolean> {
        if (this.opslaanGepauzeerd) {
            return false;
        }
        this.opslaanGepauzeerd = true;
        setTimeout(() => this.opslaanGepauzeerd = false, OPSLAAN_PAUZE_MS);
        return this.slaOpMetEigenschappen();
    }

    /**
     * Slaat het model op en gaat verder in een kopie onder een nieuwe naam. De undo-geschiedenis
     * loopt gewoon door.
     */
    async kopieer(): Promise<void> {
        this.videoStatus.cancel();
        if (!await this.bewaar()) {
            this.log.error('fout bij opslaan - stop kopie');
            return;
        }
        const titel = this.dlModel.titel;
        //eindigt de titel op " - <getal>", dan wordt het het volgende getal
        const nummer = titel.match(/^(.*) - (\d+)$/);
        const titelsuggestie = nummer ? `${nummer[1]} - ${parseInt(nummer[2], 10) + 1}` : `${titel} - 2`;
        const props = await this.opslag.propertiesDialog('MODELPROPS_COPY', {titel: titelsuggestie}, true);
        if (!props) {
            return; //geannuleerd
        }
        //de buffer is leeg, want net opgeslagen; de kopieerregel komt dus in het nieuwe model
        this.opslag.logaction('copymodel', 'model', null, 'model', {
            from: this.opslag.modelId(),
            fromname: this.dlModel.titel
        }, false);
        this.opslag.ontkoppel();
        this.opslag.setProperties(props);
        if (!await this.bewaar(true)) {
            void this.meldNietOpgeslagen();
        }
    }

    /**
     * De knop "eigenschappen wijzigen". Een nog niet opgeslagen model gaat naar `slaOp()`, dat ook
     * om een naam vraagt.
     */
    async wijzigNaam(): Promise<void> {
        this.videoStatus.cancel();
        if (!this.opslag.isOpgeslagen()) {
            await this.slaOp();
            return;
        }
        const oudetitel = this.dlModel.titel;
        if (!await this.opslag.propertiesDialog('MODELPROPS_WIJZIG')) {
            return; //geannuleerd
        }
        this.opslag.logaction('modifymodel', 'model', null, 'model', {
            oldname: oudetitel,
            name: this.dlModel.titel
        }, false);
        await this.bewaar();
    }

    /**
     * De modelloader: kies een eigen model om te laden, of bij meekijken een model van een ander.
     * Het huidige model wordt eerst opgeslagen; lukt dat niet, dan blijft het staan.
     * @param [meekijken] toon de modellen die voor meekijken openstaan
     */
    async laadMenu(meekijken = false): Promise<void> {
        this.videoStatus.cancel();
        const modellen = await this.api.getAllModels(meekijken);
        try {
            const keuze = await this.openModelLoader(modellen, this.opslag.modelId(), meekijken,
                modelId => this.api.deleteModel(modelId));
            if (keuze === null) {
                return; //geannuleerd
            }
            this._laden.set(true);
            //`false` is "nieuw model": dan is de reset hieronder het hele werk
            if (await this.nieuw() && keuze) {
                if (meekijken) {
                    await this.laadMeekijkmodel(keuze.id);
                } else {
                    await this.opslag.laad(keuze.id);
                }
            }
        } catch (fout) {
            if (fout !== 'cancel' && fout !== 'escape key press') {
                this.log.warn(fout);
            }
        } finally {
            this._laden.set(false);
        }
    }

    /**
     * Opent een model vanuit een docentendashboard: meekijken met dat van een ander, of gewoon
     * openen als het van de gebruiker zelf is. Beide dashboards gaan eerst dicht.
     *
     * Meekijken loopt via een meekijkmodel: het backend maakt een sub onder de eigen userproject en
     * die volgt de broadcast van het origineel. Voor een eigen model bestaat die omweg niet
     * (`gaMeekijken` sluit eigen modellen zelfs uit).
     * @param modelId het te openen model
     * @param eigenModel of het model van de ingelogde gebruiker zelf is
     */
    async meekijken(modelId: ModelId, eigenModel: boolean): Promise<void> {
        if (!this.sessie.magMeekijken()) {
            return;
        }
        this.log.debug('meekijken', modelId, eigenModel);
        this.dashboardv1.sluiten();
        this.dashboardv2.sluiten();
        if (!modelId) {
            return;
        }
        this._laden.set(true);
        try {
            if (!await this.nieuw()) {
                return; //het huidige model kon niet opgeslagen worden: we blijven waar we zijn
            }
            if (eigenModel) {
                await this.opslag.laad(modelId);
                return;
            }
            //de laadketen zet de notifyId op de primary en registreert de samenwerking zelf
            const {sub} = await this.api.gaMeekijken(modelId);
            await this.opslag.laad(sub);
        } catch (fout) {
            this.log.warn('Fout bij meekijken vanuit dashboard', fout);
            this.toast.toon('DASHBOARDV2.KANMODELNIETOPENEN', 'error');
        } finally {
            this._laden.set(false);
        }
    }

    /**
     * Wisselt naar een ander project van dezelfde gebruiker: opslaan, het model wissen en de pagina
     * herladen op de nieuwe sessie. Mislukt de wissel, dan volgt uitloggen.
     * @param projectId het project waarheen gewisseld wordt
     */
    async wisselProject(projectId: number | string): Promise<void> {
        await this.bewaar();
        void this.opslag.nieuw(true); //wis de modeldata, voor het geval de herlaad niet werkt
        if (await this.sessie.switchProject(projectId)) {
            this.log.debug('SWITCH');
            location.reload(); //hard herladen nu de sessie is bijgewerkt
            return;
        }
        this.log.warn('Switch project mislukt');
        await this.logUit();
    }

    /**
     * Logt uit: opslaan, en alleen als dat lukt het model wissen, de sessie beëindigen en de
     * pagina herladen op het loginscherm.
     */
    async logUit(): Promise<void> {
        if (!await this.bewaar()) {
            return;
        }
        await this.opslag.nieuw(true); //wis de modeldata, voor het geval de herlaad niet werkt
        this.sessie.logout();
        location.hash = '#!/';
        location.reload();
    }

    /**
     * De samenwerkknop: de simulatie en de video gaan uit, het model wordt opgeslagen en daarna
     * gaat de samenwerkdialoog open.
     */
    async openSamenwerken(): Promise<void> {
        if (!this.magSamenwerken()) {
            return;
        }
        this.videoStatus.cancel();
        this.stopSimulatie();
        if (await this.bewaar(false)) {
            this.samenwerking.openSamenwerkdialoog();
        }
    }

    /**
     * De opslagstap vóór een simulatie. Een model dat al is opgeslagen mag meteen door. Een nog nooit
     * opgeslagen model krijgt eerst een melding waarom de opslaandialoog komt, en dan die dialoog;
     * dezelfde volgorde als bij het opzetten van een samenwerking (`maakPrimary`).
     * @returns of de simulatie door mag gaan: het model is (nu) opgeslagen; `false` bij annuleren
     */
    async slaOpVoorSimulatie(): Promise<boolean> {
        if (this.opslag.isOpgeslagen()) {
            return true;
        }
        await this.sihwconfirm.ok(this.translate.instant('SIMULATIE.TITEL'),
            this.translate.instant('SIMULATIE.EERSTOPSLAAN'), this.translate.instant('ALGEMEEN.OK'));
        return await this.slaOpMetEigenschappen() && this.opslag.isOpgeslagen();
    }

    /* ***************** DE SAMENWERKING ***************** */

    /**
     * Haalt de samenwerkstate van het model op, met de eigen weergavenaam erbij. Een nog niet
     * opgeslagen model heeft er nog geen: dan `null`.
     */
    async haalSamenwerkState(): Promise<SamenwerkState | null> {
        const modelId = this.opslag.modelId();
        if (!modelId) {
            return null;
        }
        const stand = await this.api.samenwerkState(modelId);
        stand.eigenNaam = this.sessie.displaynaam();
        return stand;
    }

    /**
     * Geeft terug of dit model voor meekijken openstaat, en zo ja door wie.
     */
    async haalMeekijker(): Promise<string | false> {
        const modelId = this.opslag.modelId();
        if (!modelId) {
            return false;
        }
        return (await this.api.checkMeekijken(modelId))?.result || false;
    }

    /**
     * Maakt dit model de primary van een nieuwe samenwerking: eerst opslaan (annuleert de gebruiker
     * daar, dan gaat het niet door), dan registreren en herladen. Is het model nog nooit opgeslagen,
     * dan zegt een melding eerst waarom de opslaandialoog komt.
     * @returns of de samenwerking is opgezet
     */
    async maakPrimary(): Promise<boolean> {
        if (!this.opslag.isOpgeslagen()) {
            //klantwens uit de testronde van fase 4: zonder deze melding kwam er zomaar een opslaandialoog
            await this.sihwconfirm.ok(this.translate.instant('SAMENWERKEN.TITEL'),
                this.translate.instant('SAMENWERKEN.EERSTOPSLAAN'), this.translate.instant('ALGEMEEN.OK'));
        }
        if (!await this.slaOpMetEigenschappen()) {
            return false;
        }
        const modelId = this.opslag.modelId();
        if (!modelId) {
            return false;
        }
        await this.api.setSamenwerkprimary(modelId);
        //de herlaadketen registreert de samenwerking ook zelf; deze tweede registratie stond al zo
        //in ng1 en blijft staan tot iemand bewijst dat hij overbodig is
        await this.samenwerkprotocol.registreerSamenwerken();
        await this.opslag.herlaadUitBackend();
        return true;
    }

    /**
     * Haakt aan bij de samenwerking met de opgegeven 3x3-code. Er wordt niet opgeslagen: dat heeft
     * `openSamenwerken` al gedaan. Het backend maakt een eigen submodel, dat zichzelf van de primary
     * kopieert; de laadketen zet de notifyId en registreert de samenwerking.
     * @param code de code van de primary, met streepjes
     * @returns `true`, of de foutcode uit het backend
     */
    async haakAan(code: string): Promise<true | string> {
        try {
            const {sub} = await this.api.createSamenwerksub(code);
            await this.opslag.laad(sub);
            return true;
        } catch (fout) {
            this.log.warn('Fout bij aanhaken als sub', fout);
            return (fout as {code?: string})?.code || 'ALGEMEEN';
        }
    }

    /**
     * Ontkoppelt de samenwerking en herlaadt het model. De bevestiging is al gevraagd.
     *
     * De aanwezigen worden hier bewust niet gewist: het backend stuurt een verse lijst zodra de
     * losgekoppelde sub zich op zijn eigen model herregistreert. Zelf leegmaken zou een meekijker
     * die gewoon blijft kijken ten onrechte uit het oogje halen.
     */
    async ontkoppelSamenwerking(): Promise<void> {
        const modelId = this.opslag.modelId();
        if (!modelId) {
            return;
        }
        await this.api.ontkoppelSamenwerking(modelId);
        void this.opslag.herlaadUitBackend();
    }

    /* ***************** DE DIALOGEN ***************** */

    /**
     * Opent de sjabloonkiezer.
     * @returns het gekozen sjabloon, `{id: false}` voor een leeg model, of `null` bij annuleren
     */
    openTemplatePicker(templates: TemplateSamenvatting[]): Promise<TemplateKeuze> {
        this.log.debug('openTemplatePicker');
        return new Promise<TemplateKeuze>(resolve => {
            const modalRef = this.modalService.show(DlTemplatePickerComponent, {
                backdrop: 'static',
                animated: true,
                class: 'dl-modal',
                initialState: {templates}
            });
            //het onHidden van déze modal, niet dat van de modalservice: dat vuurt voor élke modal
            //en resolvet deze promise voortijdig met null als kort ervoor een andere modal (de
            //startup-popup) nog aan het sluiten is
            const sub = modalRef.onHidden?.subscribe(() => {
                sub?.unsubscribe();
                resolve(modalRef.content?.resultaat ?? null);
            });
        });
    }

    /**
     * Opent de modelloader.
     * @param modellen de modellen om uit te kiezen
     * @param huidigModelId het geopende model, dat niet in de lijst komt
     * @param meekijken of het om de modellen voor meekijken gaat
     * @param [verwijderCallback] verwijdert een model vanuit de lijst
     * @returns het gekozen model, `false` voor een nieuw model, of `null` bij annuleren
     */
    openModelLoader(
        modellen: ModelSamenvatting[],
        huidigModelId: ModelId | null,
        meekijken: boolean,
        verwijderCallback?: (modelId: ModelId) => Promise<unknown>
    ): Promise<ModelKeuze> {
        this.log.debug('openModelLoader');
        return new Promise<ModelKeuze>(resolve => {
            const modalRef = this.modalService.show(DlModelLoaderComponent, {
                backdrop: 'static',
                animated: true,
                class: 'dl-modal',
                initialState: {
                    modellen,
                    huidigModelId,
                    meekijken,
                    verwijderCallback
                }
            });
            //het onHidden van déze modal; zie openTemplatePicker
            const sub = modalRef.onHidden?.subscribe(() => {
                sub?.unsubscribe();
                resolve(modalRef.content?.resultaat ?? null);
            });
        });
    }

    /**
     * Opent de startup-popup. De keuzes komen binnen via de drie callbacks.
     */
    openStartupPopup(
        projectdata: StartupPopupProjectdata,
        userdata: StartupPopupUserdata,
        onStartvideo: () => void,
        onOpenmodel: () => void,
        onStandaardmodel: () => void
    ): void {
        this.log.debug('openStartupPopup');
        this.modalService.show(DlStartupPopupComponent, {
            backdrop: 'static',
            keyboard: false,
            animated: true,
            class: 'dl-modal',
            initialState: {projectdata, userdata, onStartvideo, onOpenmodel, onStandaardmodel}
        });
    }

    /* ***************** PRIVATE ***************** */

    /**
     * Slaat het model op; een nog niet opgeslagen model vraagt eerst zijn eigenschappen. Zonder de
     * rem van de opslaanknop: die hoort bij de knop, niet bij een flow die toevallig vlak na een klik
     * op opslaan komt. Met die rem ging "samenwerken" bij een nieuw model binnen 5 s na opslaan stil
     * dicht (gevonden in de testronde van fase 4; ng1 deed dat ook).
     * @returns of het model is opgeslagen; `false` ook bij annuleren
     */
    private async slaOpMetEigenschappen(): Promise<boolean> {
        this.videoStatus.cancel();
        if (this.opslag.isOpgeslagen()) {
            return this.bewaar(); //niet forceren, wel meteen
        }
        if (!await this.opslag.propertiesDialog('MODELPROPS_NIEUW')) {
            return false; //geannuleerd
        }
        const gelukt = await this.bewaar(true);
        if (!gelukt && !this.opslag.isOpgeslagen()) {
            void this.meldNietOpgeslagen();
        }
        return gelukt;
    }

    /**
     * De opstart zonder actie in de url. In de ontwikkelomgeving kan `DL.quickLoad` in de
     * localconfig meteen een model laden; anders volgt de startup-popup als het project die heeft,
     * of het standaardmodel met de startvideo.
     */
    private async standaardOpstart(): Promise<void> {
        const quickLoad = new LocalConfig().$('DL.quickLoad', null) as QuickLoad | null;
        const quickLoadModel = typeof quickLoad?.model === 'object' ? quickLoad.model[this.sessie.naam()] : quickLoad?.model;
        const quickLoadId = quickLoadModel ? await this.zoekQuickLoadModel(quickLoadModel) : null;
        if (quickLoad && quickLoadId) {
            this.log.debug(`quickLoad ${quickLoadModel}: ${quickLoadId}`);
            await this.opslag.laad(quickLoadId);
            if (quickLoad.startSim) {
                setTimeout(() => this.simulatie.startOfStop(), 1000);
            }
            return;
        }
        const projectdata = this.sessie.projectdata();
        if (!projectdata?.popup) {
            void this.openStandaardmodel();
            this.videoStatus.vraagStartvideo();
            return;
        }
        const enkelModel = this.sessie.enkelModel();
        this.openStartupPopup(
            projectdata,
            {nummodellen: this.sessie.aantalModellen(), enkelmodel: enkelModel},
            () => {
                if (this.sessie.aantalModellen() && enkelModel) {
                    void this.opslag.laad(enkelModel);
                } else {
                    void this.openStandaardmodel();
                }
                this.videoStatus.vraagStartvideo();
            },
            async () => {
                if (enkelModel) {
                    await this.opslag.laad(enkelModel);
                    return;
                }
                await this.openStandaardmodel();
                await this.laadMenu(false);
            },
            () => void this.openStandaardmodel()
        );
    }

    /**
     * Zoekt het model van quickLoad op. Een model-id gaat zoals het is; anders is het een titel, en
     * dan zoekt deze methode in de modellen van de ingelogde gebruiker, zonder op hoofdletters te
     * letten. Hebben meer modellen die titel, dan het laatst gewijzigde.
     * @param model het model-id of de titel uit de localconfig
     * @returns het model-id, of `null` als er geen model met die titel is
     */
    private async zoekQuickLoadModel(model: string): Promise<ModelId | null> {
        if (MODEL_ID_VORM.test(model)) {
            return model;
        }
        const titel = model.trim().toLowerCase();
        const gevonden = (await this.api.getAllModels() as ModelSamenvatting[])
            .filter(kandidaat => kandidaat.titel?.trim().toLowerCase() === titel)
            .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
        if (!gevonden.length) {
            this.log.warn(`quickLoad: geen model met de titel "${model}"`);
            return null;
        }
        return gevonden[0].id;
    }

    /**
     * Opent het standaardmodel van het project, als dat er is: als normmodel of als sjabloon.
     */
    private async openStandaardmodel(): Promise<void> {
        const standaardmodel = this.sessie.standaardmodel();
        if (!standaardmodel) {
            return;
        }
        switch (this.sessie.standaardmodelType()) {
            case 'norm':
                this.log.debug('Open standaard norm');
                await this.opslag.nieuwMetNormmodel(standaardmodel);
                return;
            case 'template':
                this.log.debug('Open standaard template');
                await this.opslag.nieuwUitSjabloon(standaardmodel);
                return;
            default:
                this.log.warn('Standaardmodel is norm noch template');
        }
    }

    /**
     * Slaat het model direct op.
     * @param [forceer] ook opslaan als er niets gewijzigd is of het model nog geen id heeft
     * @param [irrelevant] tel dit niet als inhoudelijke wijziging
     * @returns of het gelukt is
     */
    private async bewaar(forceer?: boolean, irrelevant?: boolean): Promise<boolean> {
        return !!await this.opslag.save(this.dlModel.modelData(), !irrelevant, true, forceer);
    }

    /**
     * Zet de simulatie uit, als hij aan staat. `stop()` zelf kijkt daar niet naar.
     * @param [skipSamenwerking] geen simulatiebericht naar de leider of de volgers
     */
    private stopSimulatie(skipSamenwerking?: boolean): void {
        if (this.simulatieStatus.simuleren()) {
            this.simulatie.stop(skipSamenwerking);
        }
    }

    /**
     * Laadt het meekijkmodel bij een model van een ander. Mislukt het, dan blijft het lege model
     * staan.
     * @param modelId het model waarmee meegekeken wordt
     */
    private async laadMeekijkmodel(modelId: ModelId): Promise<void> {
        try {
            //het sub-id is het meekijkmodel, dat zichzelf van de primary kopieert; de laadketen zet de
            //notifyId op de primary en registreert de samenwerking zelf
            const {sub} = await this.api.gaMeekijken(modelId);
            await this.opslag.laad(sub);
        } catch (fout) {
            this.log.warn('Fout bij meekijken', fout);
        }
    }

    /**
     * Meldt dat het model nog niet is opgeslagen.
     */
    private meldNietOpgeslagen(): Promise<void> {
        return this.foutmelding.toonTekst(this.translate.instant('ERROR.SAVE_MODEL_NOT_SAVED_YET'));
    }
}
